import * as React from 'react';
import { Query, compose } from 'react-apollo';
import idx from 'idx';

import Group from 'entities/Group';
import NavigationTabs from 'components/NavigationTabs';
// @ts-ignore
import UnreadStore from 'stores/UnreadStore';

import GROUP_NAV_TABS_QUERY from 'groups/components/queries/GroupNavigationTabs.gql';
import GROUP_NAV_TABS_ORGANIZATIONS_QUERY from 'groups/components/queries/GroupNavigationTabsOrganization.gql';
import ROLE_REQUESTS_COUNT_QUERY from 'roleRequests/containers/queries/RoleRequestsCountWithOrgIdQuery.gql';
import GROUP_MEMBERSHIP_REQUESTS_COUNT_QUERY from 'groupMembershipRequests/containers/queries/GroupMembershipRequestsCountQuery.gql';
import { GroupNavigationTabsQuery } from 'groups/components/queries/__generated__/GroupNavigationTabsQuery';
import {
  GroupNavigationTabsOrganization,
  GroupNavigationTabsOrganizationVariables,
  GroupNavigationTabsOrganization_organizations_permissions_adminConsole,
} from 'groups/components/queries/__generated__/GroupNavigationTabsOrganization';
import {
  RoleRequestsCountWithOrgId,
  RoleRequestsCountWithOrgIdVariables,
} from 'roleRequests/containers/queries/__generated__/RoleRequestsCountWithOrgId';
import {
  GroupMembershipRequestsCountQuery,
  GroupMembershipRequestsCountQueryVariables,
} from 'groupMembershipRequests/containers/queries/__generated__/GroupMembershipRequestsCountQuery';
import { rootGroupCode } from 'entities/RootGroup';

export function showPeopleTab(group: Group): boolean {
  return group.getType() !== 'district' && !group.isRoot();
}

function showFilesTab(group: Group, currentUser: any): boolean {
  if (!group || !currentUser || group.isStaffClass()) {
    return false;
  }

  return currentUser.has_files;
}

type Props = {
  adminTheme?: boolean;
  group: Group;
  hasFeature?: boolean;
};

type PropsGQL = {
  data?: GroupNavigationTabsQuery;
  orgsData?: GroupNavigationTabsOrganization & { loading?: boolean };
  roleRequestsCount?: number;
  groupMembershipRequestsCount?: number;
};

type State = {
  unreadCount: number;
};

class GroupNavigationTabs extends React.Component<Props & PropsGQL, State> {
  state = {
    unreadCount: 0,
  };

  static getStores() {
    return [UnreadStore];
  }

  static calculateState(state: State, props: Props) {
    const { group } = props;
    const groupUuid = group.getUuid();

    if (!groupUuid) {
      return {};
    }

    const isDistrict = group.getType() === 'district';
    const org = group.getOrganization();
    const orgUuid = org ? org.get('uuid') : null;
    const unreadCount =
      isDistrict && orgUuid
        ? UnreadStore.getUnreadCount(groupUuid) + UnreadStore.getUnreadCount(orgUuid)
        : UnreadStore.getUnreadCount(groupUuid);
    return {
      // $FlowFixMe possible null
      unreadCount,
    };
  }

  hasUnreads = () => {
    return this.state.unreadCount > 0;
  };

  hasRoleRequests = () => {
    const { roleRequestsCount } = this.props;

    if (roleRequestsCount) {
      return roleRequestsCount > 0;
    }

    return false;
  };

  hasGroupMembershipRequests = () => {
    const { groupMembershipRequestsCount } = this.props;

    if (groupMembershipRequestsCount) {
      return groupMembershipRequestsCount > 0;
    }

    return false;
  };

  render() {
    const { adminTheme, group, data, orgsData, hasFeature, ...others } = this.props;

    const user = data ? data.user : null;

    // @ts-ignore idx not working in TS...
    const canPhoneCall = !!idx(user, _ => _.permissions.canMakePhoneCalls);

    // @ts-ignore idx not working in TS...
    const permissions = idx(orgsData, _ => _.organizations[0].permissions.adminConsole);
    const adminPermissions: Partial<
      GroupNavigationTabsOrganization_organizations_permissions_adminConsole
    > = permissions || {};

    const baseUrl = `/classes/${group.getCodeOrError()}`;
    const tabs = [];

    if (adminPermissions.seeHomeTab) {
      tabs.push({
        name: 'Overview',
        path: `${baseUrl}/home`,
      });
    }

    if (group.get('hasMessagesTab')) {
      tabs.push({
        name: 'Messages',
        path: `${baseUrl}/messages`,
        indicator: this.hasUnreads(),
      });
    }

    if (showFilesTab(group, user)) {
      tabs.push({
        name: 'Files',
        path: `${baseUrl}/files`,
      });
    }

    if (showPeopleTab(group)) {
      tabs.push({
        name: 'People',
        path: `${baseUrl}/people`,
        indicator: this.hasRoleRequests() || this.hasGroupMembershipRequests(),
      });
    }

    if (canPhoneCall) {
      tabs.push({
        name: 'Calls',
        path: `${baseUrl}/calls`,
      });
    }

    if (adminPermissions.seeClassesTab) {
      tabs.push({
        name: 'Classes',
        path: `${baseUrl}/classes`,
      });
    }

    if (adminPermissions.seeSchoolsTab) {
      tabs.push({
        name: 'Schools',
        path: `${baseUrl}/schools`,
      });
    }

    if (adminPermissions.seeStatisticsTab) {
      tabs.push({
        name: 'Statistics',
        path: `${baseUrl}/statistics`,
      });
    }

    if (adminPermissions.seeSettingsTab) {
      tabs.push({
        name: 'Settings',
        path: `${baseUrl}/settings/organization`,
      });
    }

    if (orgsData && orgsData.loading) {
      return null;
    }

    if (!group.isStaffClass() && !group.isRoot()) {
      tabs.push({
        name: 'Settings',
        path: `${baseUrl}/class_settings`,
      });
    }

    return (
      <NavigationTabs
        {...others}
        tabs={tabs}
        theme={adminTheme ? 'light' : 'dark'}
        className="GroupNavigationTabs"
      />
    );
  }
}

const withGroupNavigationTabsData = (WrappedComponent: React.ComponentType<Props & PropsGQL>) => {
  return class extends React.Component<Props & PropsGQL, State> {
    render() {
      return (
        <Query<GroupNavigationTabsQuery, {}> query={GROUP_NAV_TABS_QUERY}>
          {({ loading, error, data }) => {
            return <WrappedComponent {...this.props} data={data} />;
          }}
        </Query>
      );
    }
  };
};

const withGroupNavigationTabsOrganizationData = (
  WrappedComponent: React.ComponentType<Props & PropsGQL>
) => {
  return class extends React.Component<Props & PropsGQL, State> {
    render() {
      const { group } = this.props;

      return (
        <Query<GroupNavigationTabsOrganization, GroupNavigationTabsOrganizationVariables>
          query={GROUP_NAV_TABS_ORGANIZATIONS_QUERY}
          variables={{ organizationId: group.getStaffClassOrgId() }}
          skip={!group.isStaffClass()}>
          {({ loading, error, data }) => {
            const orgsData = data ? { ...data, loading } : undefined;
            return <WrappedComponent {...this.props} orgsData={orgsData} />;
          }}
        </Query>
      );
    }
  };
};

const withRoleRequestsCount = (WrappedComponent: React.ComponentType<Props & PropsGQL>) => {
  return class extends React.Component<Props & PropsGQL, State> {
    render() {
      const { group } = this.props;

      return (
        <Query<RoleRequestsCountWithOrgId, RoleRequestsCountWithOrgIdVariables>
          query={ROLE_REQUESTS_COUNT_QUERY}
          variables={{
            organizationId: group.getStaffClassOrgId() as number,
            role: 'teacher',
          }}
          fetchPolicy="network-only"
          skip={!group.isStaffClass()}>
          {({ loading, error, data }) => {
            const roleRequestsCountData =
              // @ts-ignore
              data && idx(data, _ => _.organizations[0].roleRequestCount);

            return (
              <WrappedComponent {...this.props} roleRequestsCount={roleRequestsCountData || 0} />
            );
          }}
        </Query>
      );
    }
  };
};

const withGroupMembershipRequestsCount = (
  WrappedComponent: React.ComponentType<Props & PropsGQL>
) => {
  return class extends React.Component<Props & PropsGQL, State> {
    render() {
      const { group } = this.props;

      return (
        <Query<GroupMembershipRequestsCountQuery, GroupMembershipRequestsCountQueryVariables>
          query={GROUP_MEMBERSHIP_REQUESTS_COUNT_QUERY}
          variables={{
            groupUuid: group.getUuid(),
          }}
          fetchPolicy="network-only"
          skip={group.getCode() === rootGroupCode || group.isStaffClass()}>
          {({ loading, error, data }) => {
            const groupMembershipRequestsCountData =
              // @ts-ignore
              data && idx(data, _ => _.classes[0].groupMembershipRequestsCount);

            return (
              <WrappedComponent
                {...this.props}
                groupMembershipRequestsCount={groupMembershipRequestsCountData || 0}
              />
            );
          }}
        </Query>
      );
    }
  };
};

export { GroupNavigationTabs };
export default compose(
  withGroupNavigationTabsData,
  withGroupNavigationTabsOrganizationData,
  withRoleRequestsCount,
  withGroupMembershipRequestsCount
)(GroupNavigationTabs);
